import { LocalStorageUtil } from '@shared/utils';
import { DynamicScriptService } from '@core/services/dynamic-script.service';
import { UserApi } from '@shared-features/services';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { StorageConstant, ConfigConstant } from '@shared/constants';
import * as Sentry from '@sentry/angular-ivy';
import { User } from '@features/users/models/user.model';
import { take, tap } from 'rxjs/operators';
import { BaseAPI } from './base-api.service';
import { AmplitudeService } from './amplitude.service';
import { environment } from '@env/environment';
import { UserDeviceService } from './user-device.service';

export function appConfigFactory(
  dynamicScriptService: DynamicScriptService,
  featureFlagService: FeatureFlagService,
  authService: UserApi,
  apiBaseService: BaseAPI,
  amplitudeService: AmplitudeService,
  userDeviceService: UserDeviceService
): () => Promise<any> {
  return () => {
    const oldVersion = LocalStorageUtil.getItem(ConfigConstant.APP_VERSION_KEY) || null;
    // Read before the clear below: the device identifies the browser, not the session,
    // so it has to survive a version upgrade the same way the tokens do.
    const userDeviceBackup = userDeviceService.getCurrentUserDevice();
    let currentUserMap: any = null;
    try {
      currentUserMap = JSON.parse(localStorage.getItem(StorageConstant.AUTH_USER)) || null;
    } catch (e) {
      console.warn('localStorage blocked (sandboxed iframe, strict browser privacy settings)', e);
    }
    let currentUser = currentUserMap ? Object.assign(new User(), currentUserMap) : null;
    let userPromise = Promise.resolve(currentUser);

    if (!oldVersion || oldVersion !== ConfigConstant.VERSION) {
      try {
        localStorage.clear();
      } catch (e) { 
        console.warn('localStorage blocked (sandboxed iframe, strict browser privacy settings)', e); 
      }
      LocalStorageUtil.setItem(ConfigConstant.APP_VERSION_KEY, ConfigConstant.VERSION);
      userDeviceService.saveUserDevice(userDeviceBackup);
      if (currentUser?.accessToken) {
        LocalStorageUtil.setItem(StorageConstant.AUTH_USER, currentUser);
        userPromise = new Promise((resolve) => {
          authService.getUserById(currentUser?.id).subscribe((user) => {
            const modifiedUser = Object.assign(user, {
              userRoles: user?.roles?.map((role) => role.userRole) || [],
            });
            resolve(modifiedUser as any);
          });
        });
      }
    }

    return userPromise.then((user) => {
      const newCurrentUser = currentUser?.accessToken
        ? Object.assign(new User(), currentUser, user, {
            userRoles: user?.roles?.map((role) => role.userRole) || [],
            accessToken: currentUser?.accessToken,
            refreshToken: currentUser?.refreshToken,
          })
        : null;

      LocalStorageUtil.setItem(StorageConstant.AUTH_USER, newCurrentUser);
      authService.currentUser$.next(newCurrentUser);

      if (newCurrentUser?.id) {
        Sentry.setUser({ id: newCurrentUser?.id, email: newCurrentUser?.email });
      }

      amplitudeService.init(newCurrentUser);

      return featureFlagService.init(newCurrentUser || ({ name: 'guest' } as any)).then(() => {
        featureFlagService.flags$
          .pipe(
            take(1),
            tap((flags) => {
              if (flags[ConfigConstant.cloudFlareFLag]) {
                apiBaseService.setEnvBaseUrl(environment.cloudFlareUrl);
              }
            })
          )
          .subscribe();
      });
    });
  };
}
